import React from "react";
import { useI18n, tx } from "@/lib/i18n";

function Simple({ title, kicker, children }) {
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
      <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">{kicker}</p>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2">{title}</h1>
      <div className="mt-6 prose prose-slate max-w-none text-slate-700 space-y-4">{children}</div>
    </div>
  );
}

export function HowItWorks() {
  const { lang } = useI18n();
  return (
    <Simple
      kicker={tx(lang, { nl: "Proces", en: "Process", de: "Prozess", es: "Proceso" })}
      title={tx(lang, { nl: "Hoe Woon Verder werkt", en: "How Woon Verder works", de: "So funktioniert Woon Verder", es: "Cómo funciona Woon Verder" })}
    >
      <p>{tx(lang, {
        nl: "Woon Verder is een matching platform. Wij verbinden verkopers direct met woningzoekers die daadwerkelijk zoeken.",
        en: "Woon Verder is a matching platform. We connect sellers directly with active, real buyers.",
        de: "Woon Verder ist eine Matching-Plattform. Wir verbinden Verkäufer direkt mit aktiven, echten Käufern.",
        es: "Woon Verder es una plataforma de match. Conectamos vendedores directamente con compradores reales y activos.",
      })}</p>
      <h2 className="font-display text-2xl font-bold mt-8">{tx(lang, { nl: "Voor verkopers", en: "For sellers", de: "Für Verkäufer", es: "Para vendedores" })}</h2>
      <ol className="list-decimal ml-6 space-y-2">
        <li>{tx(lang, {
          nl: "U meldt uw woning aan via ons formulier — inclusief foto's óf een aanvraag voor Woon Verder-fotografie.",
          en: "You list your property via our form — including photos or a request for Woon Verder photography.",
          de: "Sie reichen Ihre Immobilie über unser Formular ein — inklusive Fotos oder einer Anfrage für Woon-Verder-Fotografie.",
          es: "Publicas tu propiedad por nuestro formulario — con fotos o pidiendo fotografía Woon Verder.",
        })}</li>
        <li>{tx(lang, {
          nl: "Uw aanvraag wordt NIET automatisch gepubliceerd. Woon Verder controleert eerst alle gegevens.",
          en: "Your submission is NEVER auto-published. Woon Verder reviews all details first.",
          de: "Ihre Einreichung wird NIE automatisch veröffentlicht. Woon Verder prüft zuerst alle Daten.",
          es: "Tu solicitud NUNCA se publica automáticamente. Woon Verder revisa primero todos los datos.",
        })}</li>
        <li>{tx(lang, {
          nl: "Wij nemen contact met u op en plannen indien nodig een verificatie-afspraak.",
          en: "We reach out and, if needed, schedule a verification appointment.",
          de: "Wir melden uns und vereinbaren bei Bedarf einen Verifikationstermin.",
          es: "Nos ponemos en contacto y, si hace falta, agendamos una cita de verificación.",
        })}</li>
        <li>{tx(lang, {
          nl: "Na goedkeuring publiceren wij de woning en matchen we met actieve zoekprofielen.",
          en: "After approval we publish the property and match it with active search profiles.",
          de: "Nach der Freigabe veröffentlichen wir die Immobilie und matchen sie mit aktiven Suchprofilen.",
          es: "Tras la aprobación publicamos la vivienda y hacemos match con perfiles de búsqueda activos.",
        })}</li>
        <li>{tx(lang, {
          nl: "Bij succesvolle verkoop geldt 2,5% (excl. btw) van de gerealiseerde verkoopprijs volgens overeenkomst.",
          en: "On successful sale a 2.5% fee (excl. VAT) applies on the final sale price, per agreement.",
          de: "Bei erfolgreichem Verkauf gelten 2,5 % (exkl. MwSt.) vom erzielten Verkaufspreis gemäß Vertrag.",
          es: "En caso de venta exitosa se aplica el 2,5 % (sin IVA) sobre el precio final de venta, según contrato.",
        })}</li>
      </ol>
      <h2 className="font-display text-2xl font-bold mt-8">{tx(lang, { nl: "Voor kopers", en: "For buyers", de: "Für Käufer", es: "Para compradores" })}</h2>
      <ol className="list-decimal ml-6 space-y-2">
        <li>{tx(lang, {
          nl: "U maakt gratis een zoekprofiel aan (na goedkeuring van uw account).",
          en: "You create a free search profile (after your account is approved).",
          de: "Sie erstellen ein kostenloses Suchprofil (nach Freigabe Ihres Kontos).",
          es: "Creas un perfil de búsqueda gratuito (tras aprobar tu cuenta).",
        })}</li>
        <li>{tx(lang, {
          nl: "U ontvangt matches met een indicatieve match-score.",
          en: "You receive matches with an indicative match score.",
          de: "Sie erhalten Matches mit einem Match-Score.",
          es: "Recibes matches con una puntuación indicativa.",
        })}</li>
        <li>{tx(lang, {
          nl: "U toont interesse, plant bezichtigingen en brengt biedingen uit via het platform.",
          en: "You express interest, book viewings and submit bids via the platform.",
          de: "Sie zeigen Interesse, buchen Besichtigungen und geben Gebote über die Plattform ab.",
          es: "Muestras interés, reservas visitas y haces ofertas por la plataforma.",
        })}</li>
      </ol>
      <p className="mt-6 text-sm text-slate-500">{tx(lang, {
        nl: "Op Woon Verder worden geen online betalingen gedaan. Zoekservice en fotografie-opdrachten worden na akkoord via factuur afgerekend.",
        en: "No online payments on Woon Verder. Search service and photography assignments are invoiced after agreement.",
        de: "Keine Online-Zahlungen bei Woon Verder. Suchservice und Fotografie werden nach Zustimmung per Rechnung abgerechnet.",
        es: "No hay pagos online en Woon Verder. El servicio de búsqueda y la fotografía se facturan tras aceptar.",
      })}</p>
    </Simple>
  );
}

export function About() {
  const { lang } = useI18n();
  return (
    <Simple
      kicker={tx(lang, { nl: "Over ons", en: "About us", de: "Über uns", es: "Sobre nosotros" })}
      title={tx(lang, { nl: "Wij zoeken verder.", en: "We search further.", de: "Wir suchen weiter.", es: "Buscamos más allá." })}
    >
      <p>{tx(lang, {
        nl: "Woon Verder bouwt aan een internationaal real-estate matching platform. Nederlandse launch. Wereldwijde ambitie.",
        en: "Woon Verder is building an international real-estate matching platform. Dutch launch. Global ambition.",
        de: "Woon Verder baut eine internationale Real-Estate-Matching-Plattform. Start in den Niederlanden. Globale Ambition.",
        es: "Woon Verder construye una plataforma internacional de match inmobiliario. Lanzamiento en Países Bajos. Ambición global.",
      })}</p>
      <p>{tx(lang, {
        nl: "Ons uitgangspunt: elke verkoper verdient de juiste koper, en elke koper verdient een platform dat écht zoekt.",
        en: "Our premise: every seller deserves the right buyer, and every buyer deserves a platform that truly searches.",
        de: "Unser Grundsatz: Jeder Verkäufer verdient den richtigen Käufer, und jeder Käufer verdient eine Plattform, die wirklich sucht.",
        es: "Nuestra premisa: cada vendedor merece el comprador adecuado, y cada comprador merece una plataforma que realmente busque.",
      })}</p>
      <p className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">{tx(lang, {
        nl: "Zoek verder naar wat bij je past.",
        en: "Search further for what fits you.",
        de: "Suche weiter nach dem, was zu dir passt.",
        es: "Busca más allá lo que te encaja.",
      })}</p>
    </Simple>
  );
}

export function Contact() {
  const { lang } = useI18n();
  return (
    <Simple
      kicker={tx(lang, { nl: "Contact", en: "Contact", de: "Kontakt", es: "Contacto" })}
      title={tx(lang, { nl: "Neem contact op", en: "Get in touch", de: "Kontakt aufnehmen", es: "Ponte en contacto" })}
    >
      <p>{tx(lang, {
        nl: "Heeft u een vraag over uw woning of over ons proces? Wij helpen u graag.",
        en: "Any question about your property or our process? We're happy to help.",
        de: "Fragen zu Ihrer Immobilie oder unserem Prozess? Wir helfen gerne.",
        es: "¿Preguntas sobre tu propiedad o nuestro proceso? Estamos aquí para ayudar.",
      })}</p>
      <p>Email: <a href="mailto:info@woonverder.eu" className="text-[color:var(--wv-navy)] font-semibold">info@woonverder.eu</a></p>
      <p>{tx(lang, {
        nl: "We reageren doorgaans binnen één werkdag.",
        en: "We usually reply within one business day.",
        de: "Wir antworten in der Regel innerhalb eines Werktages.",
        es: "Solemos responder en un día laborable.",
      })}</p>
    </Simple>
  );
}
